import { type ReactNode, useCallback } from "react";
import { Box, Typography } from "@mui/material";
import { type QueryKey, useQuery } from "@tanstack/react-query";
import { Modal } from "@packsize/components";
import { FormattedMessage } from "react-intl";

import { Loading } from "@/components/loading";
import { packNetAxios } from "@/lib/api-client";
import { DiffView, type DiffViewActionColumn } from "@/components/diff-view";
import { FailedRetry } from "@/components/failed-retry";
import { baseRoutes } from "@/api/base-routes";
import type { ConfigurationAuditEntry } from "@/schemas/audit/configuration-audit";

type AuditHistoryRecord = ConfigurationAuditEntry & {
	defaultExpanded?: boolean;
};

const getHistoryData = async ({ queryKey }: { queryKey: QueryKey }): Promise<AuditHistoryRecord[] | null> => {
	const [entityHistoryUrl] = queryKey;
	if (entityHistoryUrl) {
		return (
			await packNetAxios.get<AuditHistoryRecord[]>(`${baseRoutes.audit}/v1/ConfigurationAudit/${entityHistoryUrl}`)
		).data;
	}
	return null;
};

const defaultTransform = <T extends Record<string, unknown>>(data: T): T => {
	delete data.UserName;
	delete data.EventUtcTimeStamp;

	return data;
};

export interface EntityHistoryModalProps<T = Record<string, unknown>> {
	readonly title?: ReactNode;
	readonly entityHistoryUrl?: string;
	readonly onClose: () => void;
	readonly autoExpand?: boolean;
	readonly showTypeColumn?: boolean;
	readonly actionColumn?: DiffViewActionColumn | null;
	readonly showActivityColumn?: boolean;
	readonly transformEntityData?: (data: T) => T;
}

export const EntityHistoryModal = <T extends Record<string, unknown> = Record<string, unknown>>({
	title,
	entityHistoryUrl,
	onClose,
	autoExpand,
	showTypeColumn,
	actionColumn,
	showActivityColumn,
	transformEntityData,
}: EntityHistoryModalProps<T>) => {
	const isDiffModalOpened = !!entityHistoryUrl;

	const {
		data: historyData,
		isLoading,
		isError,
		refetch,
	} = useQuery({
		queryKey: [entityHistoryUrl],
		queryFn: getHistoryData,
		retryOnMount: true,
	});

	const composedTransform = useCallback(
		(data: unknown) => {
			if (transformEntityData) {
				return transformEntityData(defaultTransform(data as T) as T);
			}
			return defaultTransform(data as T);
		},
		[transformEntityData],
	);

	const hasHistoryData = (historyData?.length ?? 0) > 0;

	let content: ReactNode;

	if (isLoading) {
		content = <Loading />;
	} else if (isError) {
		content = <FailedRetry retry={() => refetch()} />;
	} else if (hasHistoryData) {
		content = (
			<DiffView
				additionalDataTransform={composedTransform}
				autoExpand={autoExpand}
				data={historyData ?? []}
				showTypeColumn={showTypeColumn}
				showActivityColumn={showActivityColumn}
				actionColumn={actionColumn}
				compareAll={false}
			/>
		);
	} else {
		content = (
			<Box
				data-testid="entity-history-empty-state"
				sx={{
					minHeight: 240,
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					textAlign: "center",
				}}
			>
				<Typography variant="body1">
					<FormattedMessage
						id="No audit history exists for this item"
						defaultMessage="No audit history exists for this item"
					/>
				</Typography>
			</Box>
		);
	}

	return (
		<Modal
			onClose={onClose}
			open={isDiffModalOpened}
			title={title}
			size="xl"
			slotProps={{
				contentContainer: {
					sx: {
						p: 0,
						minHeight: 0,
						flex: 1,
					},
				},
			}}
		>
			{content}
		</Modal>
	);
};
